import React from 'react';

// ════════════════════════════════════════════════════════════════════════════
// statusTarefa.jsx — r214
// ────────────────────────────────────────────────────────────────────────────
// Rótulo, cor e ícone SVG de cada `status_execucao` de tarefa.
//
// Os rótulos e as cores são os mesmos já usados no browse de Tarefas, no
// CardTarefaCompacto, na barra de filtros e no Dashboard — que hoje repetem
// o mapa cada um por conta própria. Este arquivo nasce como a versão única
// (e a primeira com ícone); as telas antigas podem migrar pra cá aos poucos.
//
// Convenção do ícone (mesma do iconesRotinas/iconesNotificacoes):
// viewBox 0 0 16 16 e `currentColor`.
//
// Uso:
//   const st = statusDaTarefa(tarefa.status_execucao);
//   {iconeDoStatus(st.codigo, { width: 14, height: 14, color: st.cor })}
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  cinzaNaoIniciado: '#CCCCCC', cinzaSt:  '#f3f4f6',
  verdeAndamento:   '#009900', verdeBg:  '#f0fff4',
  amarelo:          '#d97706', amlBg:    '#fffbeb',
  // r220: Pausado virou azul claro e Pendente Cham. Filho virou laranja.
  azulClaroPausado: '#38BDF8', azulClaroPausadoBg: '#f0f9ff',
  laranjaFilho:     '#F97316', laranjaFilhoBg:     '#fff7ed',
  amareloRetorno:   '#eab308', amlRetBg: '#fefce8',
  azul:             '#003399', azulBg:   '#eff6ff',
  vermelho:         '#dc2626', vermBg:   '#fff0f0',
  laranja:          '#f97316', laranjaBg:'#fff7ed',
};

const SVGS = {
  // Círculo vazado — não iniciado
  N: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2.5 2.2"/>
    </svg>
  ),
  // Play — em andamento
  E: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6.6 5.4l4.2 2.6-4.2 2.6V5.4z" fill="currentColor"/>
    </svg>
  ),
  // Pause — pausado
  X: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6.4 5.4v5.2M9.6 5.4v5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  // Seta de retorno — pendente de retorno
  R: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M13.5 8a5.5 5.5 0 11-1.8-4.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M12.4 1.4v2.8H9.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Ramificação — pendente de chamado filho
  P: (
    <svg viewBox="0 0 16 16" fill="none">
      <circle cx="4" cy="3.5" r="1.9" fill="currentColor"/>
      <circle cx="12" cy="12.5" r="1.9" fill="currentColor"/>
      <circle cx="4" cy="12.5" r="1.9" fill="currentColor"/>
      <path d="M4 5.4v7.1M4 8.5h5a3 3 0 013 3v.4"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Bandeira — finalizado
  F: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M3.5 14.5V2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M3.5 2.6h8.2l-1.7 2.7 1.7 2.7H3.5V2.6z"
            fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    </svg>
  ),
  // Frasco — sandbox
  S: (
    <svg viewBox="0 0 16 16" fill="none">
      <path d="M6.5 1.8v4L3.2 12a1.6 1.6 0 001.4 2.4h6.8A1.6 1.6 0 0012.8 12L9.5 5.8v-4"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M5.6 1.8h4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M5 9.6h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

// Interrogação — status desconhecido (código novo ainda não mapeado aqui)
const SVG_GENERICO = (
  <svg viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6.3 6.2a1.8 1.8 0 013.4.8c0 1.2-1.7 1.3-1.7 2.4"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="8" cy="11.4" r="0.85" fill="currentColor"/>
  </svg>
);

// Ordem = a mesma do browse de Tarefas.
export const STATUS_TAREFA = [
  { codigo: 'N', label: 'Não Iniciado',          cor: COR.cinzaNaoIniciado, bg: COR.cinzaSt   },
  { codigo: 'E', label: 'Em Andamento',          cor: COR.verdeAndamento,   bg: COR.verdeBg   },
  { codigo: 'X', label: 'Pausado',               cor: COR.azulClaroPausado, bg: COR.azulClaroPausadoBg },
  { codigo: 'R', label: 'Pendente de Retorno',   cor: COR.amareloRetorno,   bg: COR.amlRetBg  },
  { codigo: 'P', label: 'Pendente Cham. Filho',  cor: COR.laranjaFilho,     bg: COR.laranjaFilhoBg },
  { codigo: 'F', label: 'Finalizado',            cor: COR.vermelho,         bg: COR.vermBg    },
  { codigo: 'S', label: 'Sandbox',               cor: COR.laranja,          bg: COR.laranjaBg },
];

const POR_CODIGO = STATUS_TAREFA.reduce((acc, s) => { acc[s.codigo] = s; return acc; }, {});

const DESCONHECIDO = {
  codigo: '?', label: 'Status indefinido', cor: '#888', bg: '#f3f4f6',
};

// Metadados do status — nunca retorna undefined.
export function statusDaTarefa(codigo) {
  return POR_CODIGO[codigo] || DESCONHECIDO;
}

// ────────────────────────────────────────────────────────────────────────────
// iconeDoStatus(codigo, options)
//   options.width / height — tamanho (default 16)
//   options.color — cor do stroke/fill (default 'currentColor')
// ────────────────────────────────────────────────────────────────────────────
export function iconeDoStatus(codigo, { width = 16, height = 16, color = 'currentColor' } = {}) {
  const svg = SVGS[codigo] || SVG_GENERICO;
  return React.cloneElement(svg, {
    width, height, style: { color, flexShrink: 0 },
  });
}
